import { DictionaryType, DictionaryMetadata, DictionaryEntry } from '../types';

const DB_NAME = 'QTCollectionDB';
const DB_VERSION = 1;

export const STORE_METADATA = 'DictionaryMetadata';
export const STORE_ENTRIES = 'DictionaryEntries';
export const INDEX_TYPE = 'type_idx';

let dbInstance: IDBDatabase | null = null;

function getIDBFactory(): IDBFactory {
  if (typeof indexedDB !== 'undefined') return indexedDB;
  if (typeof self !== 'undefined' && self.indexedDB) return self.indexedDB;
  throw new Error('IndexedDB không được hỗ trợ trong môi trường này.');
}

export function getDB(): Promise<IDBDatabase> {
  if (dbInstance) {
    return Promise.resolve(dbInstance);
  }

  const idbFactory = getIDBFactory();

  return new Promise((resolve, reject) => {
    const request = idbFactory.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(STORE_METADATA)) {
        db.createObjectStore(STORE_METADATA, { keyPath: 'type' });
      }

      if (!db.objectStoreNames.contains(STORE_ENTRIES)) {
        const entryStore = db.createObjectStore(STORE_ENTRIES, { keyPath: ['type', 'key'] });
        entryStore.createIndex(INDEX_TYPE, 'type', { unique: false });
      }
    };

    request.onsuccess = (event) => {
      dbInstance = (event.target as IDBOpenDBRequest).result;

      dbInstance.onclose = () => {
        dbInstance = null;
      };

      resolve(dbInstance);
    };

    request.onerror = (event) => {
      reject((event.target as IDBOpenDBRequest).error);
    };
  });
}

function handleIDBError(error: any): never {
  if (error && (error.name === 'QuotaExceededError' || error.code === 22)) {
    throw new Error('Dung lượng lưu trữ trình duyệt (IndexedDB) đã đầy. Vui lòng giải phóng bộ nhớ thiết bị hoặc xóa bớt từ điển cũ.');
  }
  throw error;
}

export async function getMetadata(type: DictionaryType): Promise<DictionaryMetadata | null> {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_METADATA, 'readonly');
    const store = transaction.objectStore(STORE_METADATA);
    const request = store.get(type);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

export async function getAllMetadata(): Promise<DictionaryMetadata[]> {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_METADATA, 'readonly');
    const store = transaction.objectStore(STORE_METADATA);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

export async function updateMetadata(metadata: DictionaryMetadata): Promise<void> {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    try {
      const transaction = db.transaction(STORE_METADATA, 'readwrite', { durability: 'relaxed' });
      const store = transaction.objectStore(STORE_METADATA);
      const request = store.put(metadata);

      request.onsuccess = () => resolve();
      request.onerror = (e) => {
        try {
          handleIDBError((e.target as IDBRequest).error);
        } catch (err) {
          reject(err);
        }
      };
    } catch (err) {
      try {
        handleIDBError(err);
      } catch (finalErr) {
        reject(finalErr);
      }
    }
  });
}

export async function countEntriesByType(type: DictionaryType): Promise<number> {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_ENTRIES, 'readonly');
    const store = transaction.objectStore(STORE_ENTRIES);
    const index = store.index(INDEX_TYPE);
    const request = index.count(IDBKeyRange.only(type));

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getEntriesByType(
  type: DictionaryType,
  offset = 0,
  limit = 300
): Promise<DictionaryEntry[]> {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_ENTRIES, 'readonly');
    const store = transaction.objectStore(STORE_ENTRIES);
    const index = store.index(INDEX_TYPE);
    const range = IDBKeyRange.only(type);

    const results: DictionaryEntry[] = [];
    let advanced = false;

    const request = index.openCursor(range);

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (!cursor) {
        resolve(results);
        return;
      }

      if (offset > 0 && !advanced) {
        advanced = true;
        cursor.advance(offset);
        return;
      }

      results.push(cursor.value);

      if (results.length < limit) {
        cursor.continue();
      } else {
        resolve(results);
      }
    };

    request.onerror = () => reject(request.error);
  });
}

export async function getAllEntriesByType(type: DictionaryType): Promise<DictionaryEntry[]> {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_ENTRIES, 'readonly');
    const store = transaction.objectStore(STORE_ENTRIES);
    const index = store.index(INDEX_TYPE);
    const range = IDBKeyRange.only(type);
    const request = index.getAll(range);

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Ghi hàng loạt (Bulk Import) theo lô 2,000 items / transaction.
 */
export async function batchUpsertEntriesFast(
  entries: DictionaryEntry[],
  onProgress?: (current: number, total: number) => void
): Promise<void> {
  if (entries.length === 0) return;
  const db = await getDB();

  const BATCH_SIZE = 2000;
  const total = entries.length;
  let processed = 0;

  for (let i = 0; i < total; i += BATCH_SIZE) {
    const chunk = entries.slice(i, i + BATCH_SIZE);

    await new Promise<void>((resolve, reject) => {
      let isAborted = false;
      const transaction = db.transaction(STORE_ENTRIES, 'readwrite', { durability: 'relaxed' });
      const store = transaction.objectStore(STORE_ENTRIES);

      transaction.oncomplete = () => {
        if (!isAborted) {
          processed += chunk.length;
          if (onProgress) onProgress(processed, total);
          resolve();
        }
      };

      transaction.onerror = (e) => {
        isAborted = true;
        try {
          handleIDBError(transaction.error || (e.target as IDBRequest)?.error);
        } catch (err) {
          reject(err);
        }
      };

      transaction.onabort = () => {
        isAborted = true;
        reject(new Error('Giao dịch IndexedDB bị hủy bỏ.'));
      };

      for (let j = 0; j < chunk.length; j++) {
        if (isAborted) break;
        try {
          store.put(chunk[j]);
        } catch (err) {
          isAborted = true;
          reject(err);
          break;
        }
      }
    });

    await new Promise((r) => setTimeout(r, 0));
  }
}

/**
 * Xóa hàng loạt (Bulk Delete) nhiều từ theo danh sách keys
 */
export async function batchDeleteEntriesFast(
  type: DictionaryType,
  keys: string[],
  onProgress?: (current: number, total: number) => void
): Promise<void> {
  if (keys.length === 0) return;
  const db = await getDB();

  const BATCH_SIZE = 2000;
  const total = keys.length;
  let processed = 0;

  for (let i = 0; i < total; i += BATCH_SIZE) {
    const chunk = keys.slice(i, i + BATCH_SIZE);

    await new Promise<void>((resolve, reject) => {
      let isAborted = false;
      const transaction = db.transaction(STORE_ENTRIES, 'readwrite', { durability: 'relaxed' });
      const store = transaction.objectStore(STORE_ENTRIES);

      transaction.oncomplete = () => {
        if (!isAborted) {
          processed += chunk.length;
          if (onProgress) onProgress(processed, total);
          resolve();
        }
      };

      transaction.onerror = (e) => {
        isAborted = true;
        reject(transaction.error || (e.target as IDBRequest)?.error);
      };

      for (let j = 0; j < chunk.length; j++) {
        if (isAborted) break;
        try {
          store.delete([type, chunk[j]]);
        } catch (err) {
          isAborted = true;
          reject(err);
          break;
        }
      }
    });

    await new Promise((r) => setTimeout(r, 0));
  }
}

export async function upsertSingleEntry(entry: DictionaryEntry): Promise<void> {
  return batchUpsertEntriesFast([entry]);
}

export async function deleteSingleEntry(type: DictionaryType, key: string): Promise<void> {
  return batchDeleteEntriesFast(type, [key]);
}

export async function clearDictionary(type: DictionaryType): Promise<void> {
  const db = await getDB();

  const keysToDelete: IDBValidKey[] = await new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_ENTRIES, 'readonly');
    const store = transaction.objectStore(STORE_ENTRIES);
    const index = store.index(INDEX_TYPE);
    const request = index.getAllKeys(IDBKeyRange.only(type));
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });

  if (keysToDelete.length > 0) {
    const BATCH_SIZE = 5000;
    for (let i = 0; i < keysToDelete.length; i += BATCH_SIZE) {
      const chunk = keysToDelete.slice(i, i + BATCH_SIZE);

      await new Promise<void>((resolve, reject) => {
        let isAborted = false;
        const transaction = db.transaction(STORE_ENTRIES, 'readwrite', { durability: 'relaxed' });
        const store = transaction.objectStore(STORE_ENTRIES);

        transaction.oncomplete = () => {
          if (!isAborted) resolve();
        };

        transaction.onerror = (e) => {
          isAborted = true;
          reject(transaction.error || (e.target as IDBRequest)?.error);
        };

        for (let j = 0; j < chunk.length; j++) {
          if (isAborted) break;
          try {
            store.delete(chunk[j]);
          } catch (err) {
            isAborted = true;
            reject(err);
            break;
          }
        }
      });

      await new Promise((r) => setTimeout(r, 0));
    }
  }

  await updateMetadata({
    type,
    wordCount: 0,
    updatedAt: Date.now(),
  });
}
